/**
 * Parse Learning Curve Activity file and generate "LearningCurve" object suitable for scoring the Learning Curve Activity
 */
import models from './legacyEngineModels';

var LearningCurve = models.LearningCurve;
var Topic = models.Topic;
var StudyPlanItem = models.StudyPlanItem;
var MC_Question = models.MC_Question;
var SC_Question = models.SC_Question;
var FB_Question = models.FB_Question;

import { isTopicWithQuestions } from './legacyEngine';
import { isLcrpAvailable } from '../macmillan';

var config = {
    disableAnswerShuffle: false,
    fromFqTool: false,
    isStandalone: false
}

/**
 *
 * @param data learning curve plaintext source data
 * @param OPTIONAL config boolean prevents MC answer shuffling when true
 * @returns {learningCurve}
 */
export function buildLearningCurve(data, configIn) {
    if (configIn) {
        config = configIn
        config.disableAnswerShuffle = configIn.testMode
        config.lcrpHeadings = false
    }
    var lines = data.split('\n');

    var learningCurve = new LearningCurve(config);

    var topic = null, parseToolInfo = null;
    var q = null;
    var skipToNextLine = false;

    while (lines.length > 0) {
        var l = lines.shift();

        // If we are excluding an LCRP_ONLY topic, skip all the lines until next topic
        if (skipToNextLine === true && l.startsWith('TOPIC:')) {
          // reset skipToNextLine
          skipToNextLine = false
        }
        else if (skipToNextLine === true) {
          continue;
        }

        // ignore comments
        if (l.startsWith('--')) {
            continue;
        }
        // skip blank lines unless we're parsing a question
        else if (q === null && /^\s*$/.test(l)) {
            continue;
        }

        // parse header items until we find a topic or question
        if (q === null && topic === null) {
            if (l.startsWith('ACTIVITY_TITLE:')) {
                learningCurve.activityTitle = l.replace(/ACTIVITY_TITLE:\s*/, '');
                continue;
            }
            else if (l.startsWith('BOOK_ID:')) {
                learningCurve.bookId = l.replace(/BOOK_ID:\s*/, '');
                continue;
            }
            else if (l.startsWith('ACTIVITY_DESCRIPTION:')) {
                learningCurve.activityDescription = l.replace(/ACTIVITY_DESCRIPTION:\s*/, '');
                continue;
            }
            else if (l.startsWith('ST:')) {
                learningCurve.score.scoreTarget = parseInt(l.replace(/ST:\s*/, ''), 10);
                continue;
            }
            else if (l.startsWith('WHOOPS_STRINGS:')) {
                parseWhoopsString(learningCurve, l);
                continue;
            }
            else if (l.startsWith('EBOOK_REF_DESCRIPTION:')) {
                learningCurve.ebookRefDescription = l.replace(/EBOOK_REF_DESCRIPTION:\s*/, '');
                continue;
            }
            else if (l.startsWith('TOOL_INFO:')) {
                learningCurve.toolInfo = l.replace(/TOOL_INFO:\s*/, '');
                parseToolInfo = parseToolInfoIntoJson(learningCurve.toolInfo)
                learningCurve.config.toolInfo = parseToolInfo
                continue;
            }
            else if (l.startsWith('LCRP_HEADINGS:')) {
                learningCurve.config.lcrpHeadings = true
                continue;
            }
        }

        if (l.startsWith('TOPIC:')) {
            // previous question is complete, do final formatting
            if (q) {
                formatQuestion(q);
            }
            // new topic, so end of any previous question
            q = null;
            // pull the 'TOPIC: ' out of the line if there
            topic = new Topic(l.replace(/^TOPIC:\s*/, ''))
            // store a self-reference to the topics array index
            topic.topicId = learningCurve.topics.length;
            // push the new topic onto the array
            learningCurve.topics.push(topic);
            learningCurve.allTopics.push(topic);
        }
        // if this line exists, remove the last topic
        else if (l.startsWith('LCRP_ONLY:') && !isLcrpAvailable()) {
          learningCurve.topics.pop();
          learningCurve.allTopics.pop();
          skipToNextLine = true;
          continue;
        }
        else if (l.startsWith('MC:')) {
            topic = null;
            // previous question is complete, do final formatting
            if (q) {
                formatQuestion(q);
            }
            q = new MC_Question;
            initQuestion(q, l, learningCurve.questions.length, learningCurve.topics[learningCurve.topics.length-1]);
            learningCurve.questions.push(q);
        }
        else if (l.startsWith('SC:')) {
            topic = null;
            // previous question is complete, do final formatting
            if (q) {
                formatQuestion(q);
            }
            q = new SC_Question;
            initQuestion(q, l, learningCurve.questions.length, learningCurve.topics[learningCurve.topics.length-1]);
            learningCurve.questions.push(q);
        }
        else if (l.startsWith('FB:')) {
            topic = null;
            // previous question is complete, do final formatting
            if (q) {
                formatQuestion(q);
            }
            q = new FB_Question;
            initQuestion(q, l, learningCurve.questions.length, learningCurve.topics[learningCurve.topics.length-1]);
            learningCurve.questions.push(q);
        }

        else if (topic) {
            parseTopicItems(learningCurve.bookId, learningCurve.topics, l, config.isStandalone);
        }

        // question parsing, resets when a new topic or question is found
        else if (q) {
            // question parsing common to multiple choice and sentence completion
            if (q.questionType === 'MC' || q.questionType === 'SC') {
                parseMcScQuestionLine(q, l);
            }
            else if (q.questionType === 'FB')  {
                parseFbQuestionLine(q, l);
            }
        }
    }

    // previous question is complete, do final formatting
    if (q) {
        formatQuestion(q);
    }

    learningCurve.score.scoreTarget = defaultTargetScore(learningCurve);

    return learningCurve;
}

export function applyConfigOverrides (activity) {
    if (config.scoreTarget) {
        activity.score.scoreTarget = parseInt(config.scoreTarget, 10)
    }

    if (Array.isArray(config.topicsExcluded)) {
        // Create a Set lookup for excluded indices
        const excludedSet = new Set(config.topicsExcluded);
        
        // Filter out excluded topics
        activity.topics = activity.topics.filter(topic => !excludedSet.has(topic.topicId));
    }

    if (config.topicSelection) {
        activity.config.topicSelection = config.topicSelection
    }

    if (config.demo) {
        activity.studentData.user_id = 'demo_user_' + config.demo
        activity.studentData.user_id_type = parent.LearningCurve ? 'arga' : 'macmillan/other'
        activity.config.itemId = 'demo_assignment_' + config.demo
    }

}

export function defaultTargetScore(lc) {
  // honor custom target score that was set within the activity/fq_tool
  if (lc.score && lc.score.scoreTarget && lc.score.scoreTarget != null && lc.score.scoreTarget > 0) {
    return lc.score.scoreTarget;
  }
  // Only consider topics with questions when calculating defaultTargetScore
  let counter = 0;
  lc.topics.forEach(function(topic) {
    if (isTopicWithQuestions(topic, lc.questions)) {
      counter++
    }
  });
  return 150 * counter;
}

export function initQuestion(q, l, questionIndex, topic) {
    q.questionIndex = questionIndex;
    q.questionType = l.substr(0, 2);
    q.questionText = l.substr(4) + '\n';
    q.topic = topic;
    q.meta = {level: 1};
}


// renamed from "st1" in legacy learning curve px_flashquiz.js
// translate from a code TO score_target
export function codeToScoreTarget(s) {
    var s = "" + s;
    var v = new Array();
    for (var i = 1; i < s.length/2; ++i) {
        v[i] = "" + s.charCodeAt(i*2-1) - 109 - (i-1);
    }
    v = v.join("") * 1;
    if (!isNaN(v) && v > 0) {
        return v;
    } else {
        return 0;
    }
}

export function parseWhoopsString(learningCurve, l) {

    var whoops_strings = l.replace(/WHOOPS_STRINGS:\s*/, "");
    var ws = whoops_strings.split("|");
    if (ws.length == 2) {

        // discard default values and use only values provides in WHOOPS_STRINGS
        learningCurve.score.correctFeedback = [];
        learningCurve.score.incorrectFeedback = [];

        var wsc = ws[0].split(/,\s*/);
        for (var j = 0; j < wsc.length; ++j) {
            learningCurve.score.correctFeedback.push(wsc[j].trim())
        }
        var wsi = ws[1].split(/,\s*/);
        for (j = 0; j < wsi.length; ++j) {
            learningCurve.score.incorrectFeedback.push(wsi[j].trim())
        }
    }
}


export function formatQuestion(question) {
    // Done with the line-by-line parsing for this question,
    // now do any other required formatting for this question.
    question.questionText = question.questionText.trim();
    /* See MLLC-1014. LCRP and LC use the same activity files, but have different path prefixes. This was a quick
     * way to correct image loading for questions in LCRP without authors having to manually edit thousands of questions.
     */
    if (isLcrpAvailable()) {
      let search = 'question_pics/';
      let replacement = '../question_pics/';
      question.questionText = question.questionText.replace(new RegExp(search, 'g'), replacement);
    }

    //MS 3/19/2013: PP-2833 (Pushing data to allLevel array)
    if (question.meta.level != undefined && question.meta.level != null) {
        question.topic.allLevel.push(question.meta.level);
    }
    //End of PP-2833

    // update the topic's question count
    question.topic.totalQuestions += 1;

    // make sure the question has a level,
    // unless we're just parsing the question
    if (question.meta.level == null && parse_questions_only === false) {
        // just put it in level 1
        question.meta.level = 1;
    }

    if (question.meta.level > 0) {
        // update the topic level count
        var lev = question.meta.level * 1;
        if (question.topic.levelCount[lev] == null) {
            question.topic.levelCount[lev] = 1;
        } else {
            question.topic.levelCount[lev] += 1;
        }
    }

    // shuffle multiple choice answers
    if (!config.disableAnswerShuffle && question.questionType === 'MC' && question.meta.never_scramble != 'true') {
        var origChoices = question.choices.slice();
        var origFeedback = question.choices_fb.slice();
        var shuffledChoices = question.choices.slice();

        shuffledChoices.sort(function() { return (Math.random() < .5) ? 1 : -1; });

        // map old indexes to new
        var newCorrectAnswerIndex = -1;
        for (var j = 0; j < origChoices.length; j++) {
            for (var k = 0; k < shuffledChoices.length; k++) {
                if (origChoices[j] === shuffledChoices[k]) {

                    question.choices[k] = origChoices[j];
                    question.choices_fb[k] = origFeedback[j];
                    if (j === question.correctAnswerIndex) {
                        newCorrectAnswerIndex = k;
                    }

                }
            }
        }
        question.correctAnswerIndex = newCorrectAnswerIndex;

    }
}


/**
 * Parse a Multiple Choice or Sentence Completion question
 * @param question
 * @param l line of Learning Curve Activity file question text (answers, metadata, etc.)
 *
 * Example:
 SC: In the sentence below, click on the prepositional phrase:

 {{In the center,}} {{this panel}} {{displays his baseball cap.}}
 *A. [[Correct, that's the prepositional phrase]]
 B. [[No, that's the subject of the sentence.]]
 C. [[No, that's the verb phrase of the sentence.]]
 */
export function parseMcScQuestionLine(question, l) {
    // the first choice has to be "a" or "A"
    var choice_re = /^[\*]?([aA|1])[\.:\)]/;
    if (question.choices.length > 0) {
        // update choice_re so that any letter is OK from here on
        choice_re = /^[\*]?(\w)[\.:\)]/;
    }

    // strip trailing space
    l = l.replace(/\s+$/, "");

    l = parseFeedback(question, l);

    // choice
    if (l.search(choice_re) > -1) {
        var ident = RegExp.$1;
        var choice = l.replace(/^[\*]?\w[\.:\)]\s*/, "");
        ident = ident.toUpperCase();

        // extract feedback if there
        var choice_text = choice.replace(/\s*\[\[(.*?)\]\]\s*$/, "");
        var choice_fb = null;
        if (choice != choice_text) {
            choice_fb = RegExp.$1;

            // if feedback is simply "correct" or "incorrect", ignore it.
            if (choice_fb.toLowerCase() == 'correct' || choice_fb.toLowerCase() == 'incorrect') {
                choice_fb = null;
            }
        }

        question.choices.push(choice_text);
        question.choices_fb.push(choice_fb);
        question.choice_ids.push(ident);

        // if line started with *, it's the correct answer
        if (l.search(/^[\*]/) > -1) {
            question.correctAnswerIndex = question.choice_ids.length - 1;
        }

    }
    // answer
    else if (l.search(/^ans(wer)?:\s*(\w)/i) > -1) {
        var correct_choice = RegExp.$2;
        correct_choice = correct_choice.toUpperCase();
        question.correctAnswerIndex = question.choice_ids.indexOf(correct_choice);
    }
    // points_possible
    else if (l.search(/^points:\s*(\d+)/i) > -1) {
        question.points_possible = RegExp.$1;
    }
    // metadata -- starts with "_[a-zA-Z]"
    else if (l.search(/^\_[a-zA-Z]/) > -1) {
        parseMetadata(question, l);
    }
    // by default add it to the question text
    else {
        question.questionText += l + "\n";
    }
}

export function parseFbQuestionLine(question, l) {

    l = l.replace(/\s+$/, "");

    l = parseFeedback(question, l);

    // points_possible
    if (l.search(/^points:\s*(\d+)/i) > -1) {
        question.points_possible = RegExp.$1;
    }
    // answer
    else if (l.search(/^(\*|ans(wer)?:)\s*(.*)/i) > -1) {
        var ans = RegExp.$3;

        // if ans includes [[...]], that's the correct feedback string
        var originalAsnwerInput = ans.replace(/\s*\[\[(.*?)\]\]\s*$/, "");
        if (originalAsnwerInput !== ans) {
            question.feedbackCorrect = RegExp.$1;

            // if feedback is simply "correct" or "incorrect", ignore it.
            if (question.feedbackCorrect.toLowerCase() == 'correct' || question.feedbackCorrect.toLowerCase() == 'incorrect') {
                question.feedbackCorrect = null;
            }
        }
        ans = originalAsnwerInput;

        // take out anything in parens and replace common entities
        ans = ans.replace(/\s*\(.*?\)/g, "");
        ans = ans.replace(/ *\&ndash; */g, "-");
        ans = ans.replace(/ *\&nbsp; */g, " ");

        // split multiple answers, which can be specified with "_"'s
        question.answers = ans.split(/_/);

        // testTerm is the first answer
        question.testTerm = question.answers[0];

        // see if the answer is numeric or string
        if (isNaN(question.testTerm * 1)) {
            question.answerType = "string";
            // Figure out how many helps the person gets -- number of non-space chars - 3
            // (dashes and dots don't count either)
            var wordChars = question.testTerm.replace(/[\s\.-]/g, "");
            question.maxHelps = wordChars.length - 3;
            if (question.maxHelps < 1) {
                question.maxHelps = 1;
            }

        } else {
            question.answerType = "numeric";
            // numeric answers get one help only
            question.maxHelps = 1;
        }
    }
    // metadata -- starts with "_[a-zA-Z]"
    else if (l.search(/^\_[a-zA-Z]/) > -1) {

        parseMetadata(question, l);
    }
    // by default add it to the question text
    else {
        question.questionText += l + "\n";
    }

}

export function parseTopicItems(bookId, topics, l, isStandalone) {
    if (l.search(/^t_item:\s*(.*)/i) == 0) {
        addTopicItem(bookId, topics, RegExp.$1);
    }
    else if(l.search(/^ebook:\s*(.*)/i) == 0) {
        if (!config.fromFqTool && topics.length > 0) {
            addTopicEbook(bookId, topics, RegExp.$1)
        }
    }

    function addTopicItem(bookId, topics, item_data) {
        // for now item_data is just the name of the item
        item_data = item_data.trim();
        if (item_data != "") {
            var t = topics[topics.length - 1];
            t.assets.push(item_data);
            t.studyPlan.push(buildStudyPlanItem(bookId, item_data, 't_item'));
        }
    }

    function addTopicEbook(bookId, topics, item_data) {
        item_data = item_data.trim();
        if (item_data != "") {
            var t = topics[topics.length - 1];
            t.ebookSections.push(item_data);
            t.studyPlan.push(buildStudyPlanItem(bookId, item_data, 'ebook'));
        }
    }

    function buildStudyPlanItem(bookId, item_data, itemType) {
        var item = new StudyPlanItem();
        item.bookId = bookId;
        item.itemType = itemType;
        item.displayValue = getAssetDisplayValue(item_data);
        item.pageId = getPageId(bookId, item_data);

        return item;

        function getPageId (bookId, s) {
            // if the ebook md field includes [[xx]] at the end, xx is the full page id
            if (s.search(/\s*\[\[(.*)\]\]\s*/) > -1) {
                return RegExp.$1
            }
            // else look for a section number at the start, and if we find one...
            else if (s.search(/^([\d\.]+)/) > -1) {
                var pageid = RegExp.$1.toString()
                // if pageid is empty, just return it.
                if (pageid === '') {
                    return ''
                }

                // convert "." to "_"
                pageid = pageid.replace(/\./g, "_")
                // get rid of any trailing "_"
                pageid = pageid.replace(/_+$/, "")

                // and return this tacked onto the end of book_id
                return bookId + "_" + pageid
            }
            return null
        }

        // remove pageIds (anything between double brackets)
        function getAssetDisplayValue (s) {
            return s.replace(/\s*\[{2}.*\]{2}/, '')
        }
    }
}



/**
 * JOliver 5/27/16: I believe this is OBSOLETE, but leaving it just in case.
 *
 * extract feedback out of the provided string, returning what's left
 *
 * @param question Question object
 * @param s line of question text
 * @returns {*}
 */
export function parseFeedback(question, s) {
    // note the order: it needs to be: feedback \n feedback_correct \n feedback_incorrect
    // we extract them in the opposite order

    // Feedback for an incorrect answer
    var hasFeedbackIncorrect = false
    if (s.search(/<feedback_incorrect>([\s\S]*)<\/feedback_incorrect>/i) > -1) {
        question.feedbackIncorrect = RegExp.$1;
        s = RegExp.leftContext + RegExp.rightContext;
        hasFeedbackIncorrect = true
        // or on its own line(s) at the very end of the question data
    } else if (s.search(/\nfeedback_incorrect:\s*([\s\S]+)/i) > -1) {
        question.feedbackIncorrect = RegExp.$1;
        s = RegExp.leftContext;
        hasFeedbackIncorrect = true
    }

    // Feedback for a correct answer
    var hasFeedbackCorrect = false
    if (s.search(/<feedback_correct>([\s\S]*)<\/feedback_correct>/i) > -1) {
        question.feedbackCorrect = RegExp.$1;
        s = RegExp.leftContext + RegExp.rightContext;
        hasFeedbackCorrect = true
        // or on its own line(s) at the very end of the question data
    } else if (s.search(/\nfeedback_correct:\s*([\s\S]+)/i) > -1) {
        question.feedbackCorrect = RegExp.$1;
        s = RegExp.leftContext;
        hasFeedbackCorrect = true
    }

    // general feedback...
    var hasFeedbackGeneral = false
    if (s.search(/<feedback>([\s\S]*)<\/feedback>/i) > -1) {
        question.feedback = RegExp.$1;
        s = RegExp.leftContext + RegExp.rightContext;
        hasFeedbackGeneral = true
        // or on its own line(s) at the very end of the question data
    } else if (s.search(/\nfeedback:\s*([\s\S]+)/i) > -1) {
        question.feedback = RegExp.$1;
        s = RegExp.leftContext;
        hasFeedbackGeneral = true
    }

    // substitute <br>'s for line breaks in feedback
    if (hasFeedbackIncorrect) {
        question.feedbackIncorrect = question.feedbackIncorrect.replace(/\n/g, "<br>");
    }
    else if (hasFeedbackCorrect) {
        question.feedbackCorrect = question.feedbackCorrect.replace(/\n/g, "<br>");
    }
    else if (hasFeedbackGeneral) {
        question.feedback = question.feedback.replace(/\n/g, "<br>");
    }

    return s;
}

// extract metadata from a line
export function parseMetadata(question, l) {
    if (l.search(/^\_(\w+):\s*(.*)/) > -1) {
        var name = RegExp.$1;
        var value = RegExp.$2;
        name = name.toLowerCase();
        value = value.trim();

        if (value != "") {
            // store the name/value pair in the item's metadata array
            question.meta[name] = value;
        }
    }
}

// reforms the pseudo-json parseToolInfo into an object
export function parseToolInfoIntoJson(toolInfoString) {
    return eval('(' + toolInfoString + ')')
}
